/**
 * Browser-side migration tool that converts each Article's legacy HTML
 * content to markdown using a hidden TipTap instance, then POSTs the
 * markdown back to the server.
 *
 * Mounted as Alpine.data('contentMigration') from `index.html.erb`.
 */
import { Editor, type Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import Image from '@tiptap/extension-image';
import { Markdown } from 'tiptap-markdown';
import {
  CalloutMarkdown,
  HighlightMarkdown,
  YoutubeMarkdown,
  BilibiliMarkdown,
} from '../components/editor-markdown';

interface InitOpts {
  totalCount: number;
  pendingCount: number;
  nextUrl: string;
  convertUrl: string;
  csrfToken: string;
}

interface Failure {
  id: number;
  title: string;
  error: string;
}

export default (opts: InitOpts) => ({
  totalCount: opts.totalCount,
  pendingCount: opts.pendingCount,
  processedCount: 0,
  running: false,
  status: '空闲',
  failures: [] as Failure[],

  _stopRequested: false,

  // Build the same TipTap extension set we use in the live editor. Wrapped
  // in a function so we can spin up a fresh Editor per article — sharing one
  // Editor across many setContent calls intermittently triggered a
  // "Applying a mismatched transaction" race in ProseMirror, and the
  // throwaway pattern is cheap enough for a one-shot migration.
  buildExtensions(): Extensions {
    return [
      StarterKit.configure({ link: { openOnClick: false } }),
      CalloutMarkdown,
      TableKit,
      Image,
      HighlightMarkdown,
      YoutubeMarkdown,
      BilibiliMarkdown,
      Markdown.configure({
        html: true,
        tightLists: true,
        bulletListMarker: '-',
        linkify: true,
        transformPastedText: false,
        transformCopiedText: false,
      }),
    ];
  },

  init() {
    // No persistent editor; created on demand inside convertOne().
  },

  destroy() {
    // Nothing to tear down.
  },

  log(line: string) {
    const el = this.$refs.log as HTMLElement;
    const ts = new Date().toLocaleTimeString();
    el.innerHTML += `<div>[${ts}] ${line}</div>`;
    el.scrollTop = el.scrollHeight;
  },

  start() {
    if (this.running) return;
    this.running = true;
    this._stopRequested = false;
    this.status = '运行中';
    this.loop();
  },

  stop() {
    this._stopRequested = true;
    this.status = '正在停止…';
  },

  async loop() {
    while (!this._stopRequested) {
      const next = await this.fetchNext();
      if (!next || next.done) {
        this.log('全部迁移完成。');
        this.status = '完成';
        this.pendingCount = 0;
        break;
      }
      try {
        const md = this.convertOne(next.content);
        await this.postConvert(next.id, md);
        this.processedCount += 1;
        this.pendingCount = next.pending_count - 1;
        this.log(`✓ #${next.id} ${next.title}`);
      } catch (e) {
        const msg = (e as Error).message || String(e);
        this.failures.push({ id: next.id, title: next.title, error: msg });
        this.log(`✗ #${next.id} ${next.title} — ${msg}`);
        // Stop on error to avoid spamming; admin can resume later.
        break;
      }
    }
    this.running = false;
    if (this.status === '正在停止…') this.status = '已暂停';
  },

  // Spin up a throwaway editor seeded with the legacy HTML, read out the
  // markdown via tiptap-markdown's serializer, then destroy it. This avoids
  // any cross-article state carry-over.
  convertOne(html: string): string {
    const editor = new Editor({
      element: this.$refs.editor as HTMLElement,
      extensions: this.buildExtensions(),
      content: html || '',
    });
    try {
      return (editor.storage as any).markdown.getMarkdown();
    } finally {
      editor.destroy();
    }
  },

  async fetchNext() {
    const r = await fetch(opts.nextUrl, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(`next: HTTP ${r.status}`);
    return r.json();
  },

  async postConvert(id: number, contentMarkdown: string) {
    const body = new FormData();
    body.append('id', String(id));
    body.append('content_markdown', contentMarkdown);
    const r = await fetch(opts.convertUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': opts.csrfToken, Accept: 'application/json' },
      body,
    });
    if (!r.ok) throw new Error(`convert: HTTP ${r.status}`);
    const json = await r.json();
    if (!json.ok) throw new Error(json.error || 'convert failed');
  },
});
